import React from 'react';
import { CreditCard, Clock } from 'lucide-react';
import { useSubscription } from '../../lib/hooks/useSubscription';
import { useStripeCheckout } from '../../lib/hooks/useStripeCheckout';

interface PaymentStatusBannerProps {
  className?: string;
}

/**
 * Avviso non bloccante durante il grace period (14gg) dopo un pagamento
 * Stripe fallito. Dopo la scadenza subentra il blocco totale (PastDueBlockedModal).
 */
export function PaymentStatusBanner({
  className = '',
}: PaymentStatusBannerProps) {
  const subscription = useSubscription();
  const { openPortal, loading } = useStripeCheckout();

  if (subscription.loading) return null;
  if (!subscription.isPastDue || !subscription.isInGracePeriod) return null;

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'long',
    });
  };

  const daysUntilExpiry = subscription.gracePeriodEndsAt
    ? Math.ceil((subscription.gracePeriodEndsAt.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
    : 0;

  return (
    <div className={`bg-red-50 border-b border-red-300 dark:bg-red-950 dark:border-red-800 ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex min-w-0 flex-1 items-start gap-2.5">
            <Clock className="w-4 h-4 mt-0.5 text-red-700 dark:text-red-200 flex-shrink-0" />
            <p className="min-w-0 text-[13px] text-red-950 dark:text-red-100">
              <span className="font-semibold">L'ultimo pagamento non è andato a buon fine.</span>{' '}
              {subscription.gracePeriodEndsAt
                ? <>Hai tempo fino al {formatDate(subscription.gracePeriodEndsAt)} ({daysUntilExpiry} {daysUntilExpiry === 1 ? 'giorno' : 'giorni'}) per aggiornare il metodo di pagamento.</>
                : 'Aggiorna il metodo di pagamento per continuare senza interruzioni.'}
            </p>
          </div>

          <button
            onClick={() => openPortal()}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-700 hover:bg-red-800 text-white text-[12px] font-semibold rounded-lg transition-colors disabled:opacity-60 flex-shrink-0 whitespace-nowrap"
          >
            <CreditCard className="w-3.5 h-3.5" />
            {loading ? 'Apertura…' : 'Aggiorna pagamento'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Versione compatta del banner per spazi ristretti
 */
export function PaymentStatusBannerCompact({
  className = ''
}: { className?: string }) {
  const subscription = useSubscription();
  const { openPortal, loading } = useStripeCheckout();

  if (subscription.loading || !subscription.isPastDue) {
    return null;
  }

  return (
    <div className={`bg-[#FBF6EE] border border-[#EFE3CC] rounded-lg p-3 ${className}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2 text-[#9A6B1F]">
          <Clock className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm font-medium">Pagamento in sospeso</span>
        </div>
        <button
          onClick={() => openPortal()}
          disabled={loading}
          className="text-xs font-medium text-[#5C4A24] hover:text-[#37352F] underline disabled:opacity-50"
        >
          {loading ? 'Caricamento...' : 'Risolvi'}
        </button>
      </div>
    </div>
  );
}
